import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  EmailTemplate,
  initializeDefaultTemplates
} from '../../firebase/services/emailService';
import EmailTemplateDesigner from '../../components/email/EmailTemplateDesigner';

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState<Partial<EmailTemplate>>({
    name: '',
    subject: '',
    body: '',
    variables: []
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  // Debug effect for templates
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('Templates state updated:', templates);
  // }

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const emailTemplates = await getEmailTemplates();
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Loaded email templates:', emailTemplates);
      // }
      setTemplates(emailTemplates || []);
    } catch (error) {
      console.error('Error loading email templates:', error);
      setError('Failed to load email templates. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template?: EmailTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData(template);
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        subject: '',
        body: '',
        variables: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      variables: []
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, formData);
        setSuccess('Template updated successfully');
      } else {
        await createEmailTemplate(formData as Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>);
        setSuccess('Template created successfully');
      }

      handleCloseDialog();
      loadTemplates();
    } catch (error) {
      setError('Failed to save template');
      console.error('Error saving template:', error);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await deleteEmailTemplate(templateId);
      setSuccess('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      setError('Failed to delete template');
      console.error('Error deleting template:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Email Templates</Typography>
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={async () => {
                try {
                  setLoading(true);
                  await initializeDefaultTemplates();
                  setSuccess('Default templates initialized');
                  loadTemplates();
                } catch (error) {
                  console.error('Error initializing default templates:', error);
                  setError('Failed to initialize default templates');
                } finally {
                  setLoading(false);
                }
              }}
              sx={{ mr: 2 }}
            >
              Initialize Default Templates
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              New Template
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <List>
          {templates.length === 0 ? (
            <ListItem>
              <ListItemText primary="No email templates found. Click 'New Template' to create one." />
            </ListItem>
          ) : (
            templates.map((template) => (
              <ListItem key={template.id}>
                <ListItemText
                  primary={template.name}
                  secondary={
                    <Box component="span">
                      <Typography variant="body2" color="text.secondary" component="span">
                        {template.subject}
                      </Typography>
                      <Box component="span" sx={{ display: 'block', mt: 1 }}>
                        {template.variables.map((variable) => (
                          <Chip
                            key={variable}
                            label={`{{${variable}}}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                            component="span"
                          />
                        ))}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleOpenDialog(template)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDelete(template.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="xl" 
          fullWidth
          fullScreen
        >
          <DialogTitle>
            {editingTemplate ? 'Edit Template' : 'New Template'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
              />

              <EmailTemplateDesigner
                template={{
                  id: editingTemplate?.id || crypto.randomUUID(),
                  name: formData.name || '',
                  subject: formData.subject || '',
                  body: formData.body || '',
                  variables: formData.variables || [],
                  createdAt: editingTemplate?.createdAt || Date.now(),
                  updatedAt: Date.now()
                }}
                onSave={async (updatedTemplate) => {
                  setFormData({
                    ...formData,
                    name: updatedTemplate.name,
                    subject: updatedTemplate.subject,
                    body: updatedTemplate.body,
                    variables: updatedTemplate.variables
                  });
                  
                  if (editingTemplate) {
                    await updateEmailTemplate(updatedTemplate.id, updatedTemplate);
                  } else {
                    await createEmailTemplate({
                      name: updatedTemplate.name,
                      subject: updatedTemplate.subject,
                      body: updatedTemplate.body,
                      variables: updatedTemplate.variables
                    });
                  }
                  loadTemplates();
                  handleCloseDialog();
                }}
                onDelete={() => {
                  if (editingTemplate) {
                    handleDelete(editingTemplate.id);
                  }
                  handleCloseDialog();
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default EmailTemplates; 